import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { Info } from "lucide-react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "dayjs/locale/ar-sa";

import messages from "../../../../../assets/locale/messages";
import { formatDate, formatWorkingHoursDuration, clx } from "../../../../../utils/Helpers";
import { UIActionModal, UICallout } from "../../../../ui";
import { TIMESHEET_CHECKOUT_STATUSES, TIMESHEET_SLOT_TABS } from "../constants";
import TimesheetCheckoutStatusBadge from "../TimesheetCheckoutStatusBadge";
import { resolveCheckoutStatus } from "../timesheetUtils";

import "./DeleteSlotModal.scss";

const DeleteSlotDetailRow = ({ label, children, withTopBorder }) => (
  <div
    className={clx(
      "delete-slot-modal__detail-row",
      withTopBorder && "delete-slot-modal__detail-row--checkout-type",
    )}
  >
    <span className="delete-slot-modal__detail-label text-body-sm">{label}</span>
    <div className="delete-slot-modal__detail-value text-label-md">{children}</div>
  </div>
);

DeleteSlotDetailRow.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
  withTopBorder: PropTypes.bool,
};

DeleteSlotDetailRow.defaultProps = {
  children: null,
  withTopBorder: false,
};

const formatSlotDate = (slot, lang) => {
  if (slot?.check_in_readable_date?.trim()) {
    return slot.check_in_readable_date;
  }

  if (!slot?.check_in_at) {
    return "-";
  }

  const locale = lang === "ar" ? "ar-sa" : "en";
  return dayjs(slot.check_in_at).locale(locale).format("D MMMM YYYY");
};

const formatSlotTime = (value) =>
  value ? formatDate(value, "HH:mm:ss") : "-";

const DeleteSlotModal = ({
  open,
  onClose,
  slot,
  slotType,
  loading,
  onConfirm,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared, timeSheet } = messages[lang];
  const copy = messages[lang].events.viewEvents.timesheetTab.deleteSlotModal;
  const checkoutCopy = timeSheet.monthlyReport;

  const title =
    slotType === TIMESHEET_SLOT_TABS.trainings
      ? copy.trainingTitle
      : copy.shiftTitle;

  const checkoutStatus = useMemo(
    () => (slot ? resolveCheckoutStatus(slot) : TIMESHEET_CHECKOUT_STATUSES.pending),
    [slot],
  );

  const checkoutLabel = useMemo(() => {
    if (checkoutStatus === TIMESHEET_CHECKOUT_STATUSES.auto) {
      return checkoutCopy.automaticCheckout;
    }

    if (checkoutStatus === TIMESHEET_CHECKOUT_STATUSES.self) {
      return checkoutCopy.selfCheckout;
    }

    return checkoutCopy.checkoutStatusPending;
  }, [checkoutCopy, checkoutStatus]);

  const handleConfirm = () => {
    if (!slot) {
      return;
    }

    onConfirm?.(slot);
  };

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={title}
      variant="reject"
      iconName="alertCircle"
      maxWidth="sm"
      PaperProps={{ className: "delete-slot-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={copy.deleteSlot}
          confirmVariant="reject"
          confirmDisabled={!slot || loading}
          onCancel={onClose}
          onConfirm={handleConfirm}
        />
      }
    >
      <UIActionModal.Divider />
      <Typography className="delete-slot-modal__message text-body-sm" component="p">
        {copy.confirmationMessage}
      </Typography>

      {slot ? (
        <div className="delete-slot-modal__summary">
          <DeleteSlotDetailRow label={copy.date}>
            {formatSlotDate(slot, lang)}
          </DeleteSlotDetailRow>
          <DeleteSlotDetailRow label={copy.checkIn}>
            {formatSlotTime(slot.check_in_at)}
          </DeleteSlotDetailRow>
          <DeleteSlotDetailRow label={copy.checkOut}>
            {formatSlotTime(slot.check_out_at)}
          </DeleteSlotDetailRow>
          <DeleteSlotDetailRow label={copy.workingHours}>
            {formatWorkingHoursDuration(slot.working_hours, lang)}
          </DeleteSlotDetailRow>
          <DeleteSlotDetailRow label={copy.checkoutType} withTopBorder>
            <TimesheetCheckoutStatusBadge
              status={checkoutStatus}
              label={checkoutLabel}
            />
          </DeleteSlotDetailRow>
        </div>
      ) : null}

      <UICallout
        variant="error"
        className="delete-slot-modal__warning"
        icon={<Info size={20} strokeWidth={2} className="ui-callout__icon" />}
        title={copy.warning}
        hasBorder={false}
      />
    </UIActionModal>
  );
};

DeleteSlotModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  slot: PropTypes.object,
  slotType: PropTypes.oneOf([
    TIMESHEET_SLOT_TABS.shifts,
    TIMESHEET_SLOT_TABS.trainings,
  ]),
  loading: PropTypes.bool,
  onConfirm: PropTypes.func,
};

DeleteSlotModal.defaultProps = {
  slot: null,
  slotType: TIMESHEET_SLOT_TABS.shifts,
  loading: false,
  onConfirm: undefined,
};

export default DeleteSlotModal;
